import { Controller } from "@hotwired/stimulus"
import {
  INVOICE_NUMBER_LABELS,
  buildAmountIndex,
  discoverMoneyColumns,
  extractWords,
  locateAmount,
  locateLabelledValue,
  selectExtensionColumn,
} from "../lib/invoice_provenance_matcher"

// The invoice number field lives in the header card, which is a sibling of this controller's
// element rather than a descendant, so a data-action on it would never route here.
const INVOICE_NUMBER_SELECTOR = "input[name='invoice[invoice_number]']"

// Long enough that dragging the pointer down a 70-row list doesn't redraw on every row,
// short enough that a deliberate hover feels immediate.
const HOVER_DELAY_MS = 70

// Amount edits arrive as a stream of input events; only rematch once the user pauses.
const ROW_REFRESH_DELAY_MS = 250

// Breathing room around the matched token so the highlight reads as a cell, not an outline
// traced onto the glyphs.
const HIGHLIGHT_PADDING = 2

// Long enough to catch the eye after a smooth scroll settles, short enough not to linger as
// a second permanent state on the row.
const REVEAL_FLASH_MS = 1600

// Connects the extracted line items on the right to the rendered PDF on the left: hovering
// or focusing a row highlights the cell that row's amount was taken from, and clicking a
// row scrolls to that cell and pins the highlight.
//
// This is a sibling of invoice-pdf-zoom rather than an extension of it. The zoom
// controller owns rendering and tells us when it has finished by dispatching
// invoice-pdf-zoom:rendered; we own the text layer, the matching and the overlays.
export default class extends Controller {
  static targets = ["pdfPane", "rows", "summary", "bannerNote", "bannerJump"]

  connect() {
    this.geometryByPage = new Map() // page number -> words in scale-1 coordinates
    this.renderedPages = new Map() // page number -> { overlay, scale }
    this.matchesByRow = new WeakMap()
    this.amountIndex = null
    this.pinnedRow = null
    this.hoverRow = null
    this.pendingRow = null
    this.indexGeneration = 0
    this.hoverTimer = null
    this.refreshTimer = null
    this.flashTimer = null
    this.unlocatedRows = []
    this.matchCounts = { located: 0, total: 0 }
    this.revealIndex = 0
    // Null until invoice-coa-totals reports in, which is also the state it reports when the
    // invoice has no total to reconcile against. Both mean "nothing to point at".
    this.deltaCents = null

    this.documentWords = []
    this.invoiceNumberMatch = null
    this.bindInvoiceNumberField()

    // That controller sits on the form above us and has already announced by now, so ask it
    // to say it again rather than waiting for the reviewer's first edit.
    this.dispatch("connected")
  }

  disconnect() {
    // Invalidates any in-flight text extraction so a late resolve can't paint over a
    // teardown or a newer render.
    this.indexGeneration += 1
    window.clearTimeout(this.hoverTimer)
    window.clearTimeout(this.refreshTimer)
    window.clearTimeout(this.flashTimer)
    this.invoiceNumberField?.removeEventListener("pointerenter", this.showInvoiceNumber)
    this.invoiceNumberField?.removeEventListener("focus", this.showInvoiceNumber)
    this.invoiceNumberField?.removeEventListener("pointerleave", this.hideInvoiceNumber)
    this.invoiceNumberField?.removeEventListener("blur", this.hideInvoiceNumber)
    this.invoiceNumberField?.removeEventListener("input", this.refreshInvoiceNumber)
  }

  // --- invoice number ---------------------------------------------------------------------
  //
  // Only the invoice number ships. The same anchoring was measured on the other header
  // fields over the 54 hand-labelled invoices and none of them earned it: the invoice total
  // is printed once, in a totals box with no dependable label (5.6% located), and the vendor
  // name has no label at all, so uniqueness is the only evidence and it lands on the wrong
  // block as often as the letterhead. A confidently wrong highlight on a header field is
  // worse than none, so those stay unbuilt rather than half-built.

  bindInvoiceNumberField() {
    this.invoiceNumberField = document.querySelector(INVOICE_NUMBER_SELECTOR)
    if (!this.invoiceNumberField) return

    this.showInvoiceNumber = () => this.drawInvoiceNumberHighlight()
    this.hideInvoiceNumber = () => this.clearHighlight("header")
    this.refreshInvoiceNumber = () => {
      this.matchInvoiceNumber()
      this.drawInvoiceNumberHighlight()
    }

    this.invoiceNumberField.addEventListener("pointerenter", this.showInvoiceNumber)
    this.invoiceNumberField.addEventListener("focus", this.showInvoiceNumber)
    this.invoiceNumberField.addEventListener("pointerleave", this.hideInvoiceNumber)
    this.invoiceNumberField.addEventListener("blur", this.hideInvoiceNumber)
    this.invoiceNumberField.addEventListener("input", this.refreshInvoiceNumber)
  }

  matchInvoiceNumber() {
    const value = this.invoiceNumberField?.value
    this.invoiceNumberMatch = value
      ? locateLabelledValue(this.documentWords, { value, patterns: INVOICE_NUMBER_LABELS })
      : null

    // Nothing is said when the number cannot be located. There is no discrepancy to explain
    // here as there is with a line amount, so an unlocated header field is only our own
    // coverage gap - and announcing it would be noise on a field that is usually right.
    if (this.invoiceNumberField) {
      this.invoiceNumberField.classList.toggle(
        "invoice-input--located",
        this.invoiceNumberMatch?.state === "located",
      )
    }
  }

  drawInvoiceNumberHighlight() {
    this.clearHighlight("header")
    if (this.invoiceNumberMatch?.state !== "located") return

    const highlight = this.drawHighlight("header", this.invoiceNumberMatch.token)
    if (!highlight) return

    // Unlike a row, the field is in the header card and the match is near the top of page 1,
    // so both are usually on screen already; scrolling would be a jolt for nothing.
    if (!this.isHighlightVisible(highlight)) this.scrollToHighlight(highlight)
  }

  // --- rendering lifecycle -------------------------------------------------------------

  async pdfRendered(event) {
    const pages = event.detail?.pages || []
    const generation = ++this.indexGeneration

    this.renderedPages = new Map()
    pages.forEach(({ pageNumber, viewport, element }) => {
      this.renderedPages.set(pageNumber, {
        overlay: this.buildOverlay(element),
        scale: viewport.scale,
      })
    })

    await this.cachePageGeometry(pages, generation)
    if (generation !== this.indexGeneration) return

    this.indexAmountColumn()
    this.matchInvoiceNumber()
    this.refreshRowMatches()
    this.restoreHighlights()
  }

  buildOverlay(pageElement) {
    const overlay = document.createElement("div")
    overlay.className = "invoice-pdf__overlay"
    pageElement.appendChild(overlay)
    return overlay
  }

  // Text extraction is cached per page for the life of the document. Zoom re-renders the
  // canvases but never changes the text layer, and geometry is stored in scale-1 units, so
  // a zoom change only has to re-project the matches rather than re-parse the PDF.
  async cachePageGeometry(pages, generation) {
    for (const { pageNumber, page } of pages) {
      if (this.geometryByPage.has(pageNumber)) continue

      const textContent = await page.getTextContent()
      if (generation !== this.indexGeneration) return

      this.geometryByPage.set(pageNumber, extractWords(textContent, page.getViewport({ scale: 1 })))
    }
  }

  // Columns are discovered across the whole document rather than per page. On a multi-page
  // invoice the last page carries only a handful of line items, which is not enough tokens
  // for the density test to tell the amount column from a totals box.
  indexAmountColumn() {
    const words = []
    this.geometryByPage.forEach((pageWords, pageNumber) => {
      pageWords.forEach((word) => words.push({ ...word, pageNumber }))
    })

    // Kept for the header fields, which search the whole document rather than one column.
    this.documentWords = words

    const column = selectExtensionColumn(discoverMoneyColumns(words))
    this.amountIndex = column ? buildAmountIndex(column.tokens) : null
  }

  // --- row matching --------------------------------------------------------------------

  refreshRowMatches() {
    if (!this.hasRowsTarget) return

    // invoice-row-edit clones a real row to make the "add missed row" placeholder, so the
    // placeholder can inherit one of our badges. Clear it here rather than teaching that
    // controller about a badge it doesn't own.
    this.rowsTarget
      .querySelectorAll(".invoice-line-item--new .invoice-provenance-badge")
      .forEach((badge) => badge.remove())

    const unlocated = []
    let located = 0
    let total = 0

    this.rowElements().forEach((row) => {
      const cents = this.readRowCents(row)

      // A blank or zeroed row has no printed extension for us to find, so flagging it would
      // be a false alarm rather than a finding. This is not a cosmetic exception: zeroing a
      // flagged row is exactly how the reviewer resolves an ordered-but-not-shipped line,
      // and the flag has to go away when they do.
      if (cents === null || cents === 0) {
        this.matchesByRow.delete(row)
        this.applyRowState(row, null)
        return
      }

      total += 1
      const match = locateAmount(this.amountIndex, cents)
      if (match.state === "located") located += 1
      else if (match.state === "missing") unlocated.push(row)

      this.matchesByRow.set(row, match)
      this.applyRowState(row, match)
    })

    this.unlocatedRows = unlocated
    this.matchCounts = { located, total }
    this.updateFindings()
  }

  // --- the mismatch story ----------------------------------------------------------------

  // invoice-coa-totals owns the reconciliation banner and recomputes it on every edit. It
  // publishes the delta; we decide how loudly to say the rest. Listening rather than reading
  // that controller's DOM keeps the two from drifting apart.
  reconciliationChanged(event) {
    const deltaCents = event.detail?.deltaCents
    this.deltaCents = Number.isFinite(deltaCents) ? deltaCents : null
    this.updateFindings()
  }

  // Slice 1 answered "where did this number come from?" on demand. This is the other half:
  // when the invoice does not tie out, the rows whose amount we could not find are, on the
  // measured corpus, overwhelmingly the ones that caused it - and saying so has to cost no
  // interaction at all. The finding is written into the existing banner rather than raised
  // somewhere else, because a second warning about one problem reads as two problems.
  updateFindings() {
    const { located, total } = this.matchCounts
    // A null delta means there is no invoice total to reconcile against, which is the same
    // situation as a reconciled one: nothing is currently wrong, so nothing gets pointed at.
    const reconciled = this.deltaCents === null || this.deltaCents === 0
    const unlocated = this.unlocatedRows.length

    // Drives the badge and amount colouring. Flagged rows stay visible when the invoice ties
    // out - the match really did fail and hiding that would overstate our coverage - but
    // they stop shouting, because there is no longer a discrepancy for them to explain.
    this.element.dataset.invoiceProvenanceReconciles = reconciled ? "true" : "false"

    this.updateBannerNote(reconciled, unlocated)
    this.updateSummary(located, total, reconciled)
  }

  updateBannerNote(reconciled, unlocated) {
    if (!this.hasBannerNoteTarget) return

    if (reconciled || unlocated === 0) {
      this.bannerNoteTarget.textContent = ""
      this.bannerNoteTarget.classList.add("invoice-is-hidden")
      this.hideBannerJump()
      return
    }

    const subject = unlocated === 1
      ? "1 line amount couldn't be found in the invoice's amount column"
      : `${unlocated} line amounts couldn't be found in the invoice's amount column`

    // Only claim the flagged rows account for the difference when they arithmetically do -
    // that is checkable. "These are why" in the general case is not, because a row can fail
    // to match by being unbilled or by defeating the matcher on an unfamiliar layout, and
    // from here those two look identical.
    const accountsForDelta = this.unlocatedCents() === this.deltaCents

    this.bannerNoteTarget.textContent = accountsForDelta
      ? ` ${subject}, coming to exactly that difference.`
      : ` ${subject}. Those rows are flagged below.`
    this.bannerNoteTarget.classList.remove("invoice-is-hidden")

    if (!this.hasBannerJumpTarget) return

    this.bannerJumpTarget.textContent = unlocated === 1 ? "Show flagged row" : "Show flagged rows"
    this.bannerJumpTarget.classList.remove("invoice-is-hidden")
  }

  hideBannerJump() {
    if (!this.hasBannerJumpTarget) return

    this.bannerJumpTarget.classList.add("invoice-is-hidden")
    this.revealIndex = 0
  }

  unlocatedCents() {
    return this.unlocatedRows.reduce((sum, row) => sum + (this.readRowCents(row) || 0), 0)
  }

  // A badge two thirds of the way down a 66-row list is not a glance. Cycles rather than
  // re-scrolling to the same place, so a second click moves to the next flagged row.
  revealNextUnlocated(event) {
    event.preventDefault()

    // The COA pills can filter rows out of the list; scrolling to one of those would look
    // like the button did nothing at all.
    const rows = this.unlocatedRows.filter((row) => row.isConnected && !row.hidden)
    if (rows.length === 0) return

    if (this.revealIndex >= rows.length) this.revealIndex = 0
    const row = rows[this.revealIndex]
    this.revealIndex += 1

    row.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" })
    this.flashRow(row)
  }

  flashRow(row) {
    window.clearTimeout(this.flashTimer)
    this.rowElements().forEach((other) => delete other.dataset.invoiceProvenanceRevealed)

    row.dataset.invoiceProvenanceRevealed = "true"
    this.flashTimer = window.setTimeout(() => {
      delete row.dataset.invoiceProvenanceRevealed
    }, REVEAL_FLASH_MS)
  }

  scheduleRowRefresh() {
    window.clearTimeout(this.refreshTimer)
    this.refreshTimer = window.setTimeout(() => {
      this.refreshRowMatches()
      this.restoreHighlights()
    }, ROW_REFRESH_DELAY_MS)
  }

  rowElements() {
    return Array.from(
      this.rowsTarget.querySelectorAll(".invoice-line-item:not(.invoice-line-item--new)"),
    )
  }

  // Mirrors invoice_coa_totals_controller#readLineTotal: the display span holds the amount
  // until a row is opened for editing, after which the form field is authoritative. Kept
  // lenient rather than reusing parseMoneyCents, because a number input legitimately holds
  // "80.6" or "80" while the user is still typing.
  readRowCents(row) {
    const form = row.querySelector("[data-invoice-row-edit-target='form']")
    const isEditing = form && !form.classList.contains("invoice-is-hidden")
    const raw = isEditing
      ? form.querySelector("[name$='[line_total]']")?.value
      : row.querySelector(".invoice-line-item__value--amount")?.textContent

    const normalized = String(raw ?? "").trim().replace(/[^0-9.-]/g, "")
    if (normalized === "") return null

    const parsed = Number.parseFloat(normalized)
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : null
  }

  // The "couldn't locate" state is the point of showing it at all: on the measured corpus
  // these rows are, with high precision, the ordered-but-not-shipped lines that make an
  // invoice's line items overshoot its total. Flag them permanently rather than letting
  // them read as a hover that quietly did nothing.
  //
  // The wording is deliberately about *us*, not about the invoice. Two different things
  // land in this bucket - a line that genuinely was not billed, and a match this matcher
  // failed to make - and we cannot tell them apart. "Not on PDF" would assert the first;
  // all we actually know is that we could not find the amount. That distinction matters
  // because the corpus this heuristic was tuned on is ~94% a single vendor, so the failure
  // mode on an unfamiliar template is real, and it should read as our uncertainty rather
  // than as a confident claim about the document.
  applyRowState(row, match) {
    const state = match ? match.state : "unknown"
    row.dataset.invoiceProvenanceState = state

    const badge = this.ensureBadge(row)
    if (!badge) return

    if (state === "missing") {
      badge.className = "invoice-provenance-badge invoice-provenance-badge--missing"
      // Short on purpose: the badge shares a truncating cell with the description, and
      // these are the rows whose description the reviewer most needs to read. The full
      // explanation lives in the tooltip, and the red amount beside it says which value.
      badge.textContent = "Not found"
      badge.title =
        "We couldn't find this amount in the invoice's amount column. " +
        "That usually means the line was ordered but not shipped - but it can also mean " +
        "the match failed on an unfamiliar layout."
      return
    }

    if (state === "ambiguous") {
      badge.className = "invoice-provenance-badge invoice-provenance-badge--ambiguous"
      badge.textContent = `${match.candidates.length} matches`
      badge.title =
        "This amount is printed more than once in the amount column, " +
        "so it can't be pinpointed."
      return
    }

    badge.className = "invoice-provenance-badge invoice-is-hidden"
    badge.textContent = ""
    badge.removeAttribute("title")
  }

  ensureBadge(row) {
    const description = row.querySelector(".invoice-line-item__value--description")
    if (!description) return null

    let badge = description.querySelector(".invoice-provenance-badge")
    if (!badge) {
      badge = document.createElement("span")
      badge.className = "invoice-provenance-badge invoice-is-hidden"
      description.appendChild(badge)
    }

    return badge
  }

  // The quiet, nothing-is-wrong version of the same information. Suppressed while the banner
  // is up: the banner is then carrying the coverage figure's consequence, and repeating the
  // raw count above it splits one finding into two.
  updateSummary(located, total, reconciled) {
    if (!this.hasSummaryTarget) return

    if (!this.amountIndex || total === 0 || !reconciled) {
      this.summaryTarget.classList.add("invoice-is-hidden")
      return
    }

    this.summaryTarget.textContent = located === total
      ? `Matched all ${total} amounts to the PDF. Hover a row to see where it came from.`
      : `Matched ${located} of ${total} amounts to the PDF; the rest are marked below. ` +
        "Hover a row to see where it came from."
    this.summaryTarget.classList.remove("invoice-is-hidden")
  }

  // --- interaction ---------------------------------------------------------------------

  // Hover is the fast path for scanning, and deliberately does not scroll the PDF: yanking
  // the viewport while the pointer runs down a long list is disorienting. If the match is
  // off-screen the row says which page it is on instead, and a click takes you there.
  rowPointerOver(event) {
    const row = this.rowFrom(event.target)

    if (!row) {
      this.clearHover()
      return
    }

    if (row === this.pendingRow) return

    window.clearTimeout(this.hoverTimer)
    this.pendingRow = row
    this.hoverTimer = window.setTimeout(() => this.showRow(row, { pin: false }), HOVER_DELAY_MS)
  }

  // Hover is inaccessible to keyboard and touch users, so focus drives the same preview.
  // Every row contains a focusable edit button, so tabbing the list walks the highlights.
  rowFocus(event) {
    const row = this.rowFrom(event.target)
    if (!row || row === this.hoverRow) return

    window.clearTimeout(this.hoverTimer)
    this.pendingRow = row
    this.showRow(row, { pin: false })
  }

  rowClick(event) {
    // The edit trigger and the row's own form controls live inside the row; leave them to
    // their own controllers.
    if (event.target.closest("button, a, input, select, textarea, label")) return

    const row = this.rowFrom(event.target)
    if (!row) return

    if (this.pinnedRow === row) {
      this.clearPin()
      return
    }

    this.showRow(row, { pin: true })
  }

  dismiss(event) {
    if (event.key !== "Escape" || !this.pinnedRow) return

    this.clearPin()
  }

  showRow(row, { pin }) {
    // Hovering the row that is already pinned would stack a second box on the same cell.
    // The pin is the stronger of the two, so leave it alone and show nothing extra.
    if (!pin && row === this.pinnedRow) {
      this.clearHighlight("hover")
      this.hoverRow = row
      return
    }

    const kind = pin ? "pinned" : "hover"
    this.clearHighlight(kind)

    if (pin) {
      this.clearHover()
      this.clearPin()
      this.pinnedRow = row
      row.dataset.invoiceProvenancePinned = "true"
    } else {
      this.hoverRow = row
    }

    const match = this.matchesByRow.get(row)
    if (!match || match.state !== "located") return

    const highlight = this.drawHighlight(kind, match.token)
    if (!highlight) return

    if (pin) {
      this.scrollToHighlight(highlight)
      this.setPageHint(row, null)
      return
    }

    this.setPageHint(row, this.isHighlightVisible(highlight) ? null : match.token.pageNumber)
  }

  drawHighlight(kind, token) {
    const page = this.renderedPages.get(token.pageNumber)
    if (!page) return null

    const highlight = document.createElement("div")
    highlight.className = `invoice-pdf__highlight invoice-pdf__highlight--${kind}`

    // Geometry is stored at scale 1; the rendered canvas is at the viewport's scale.
    highlight.style.left = `${(token.x0 * page.scale) - HIGHLIGHT_PADDING}px`
    highlight.style.top = `${(token.y0 * page.scale) - HIGHLIGHT_PADDING}px`
    highlight.style.width = `${((token.x1 - token.x0) * page.scale) + (HIGHLIGHT_PADDING * 2)}px`
    highlight.style.height = `${((token.y1 - token.y0) * page.scale) + (HIGHLIGHT_PADDING * 2)}px`

    page.overlay.appendChild(highlight)
    return highlight
  }

  clearHighlight(kind) {
    this.renderedPages.forEach(({ overlay }) => {
      overlay.querySelectorAll(`.invoice-pdf__highlight--${kind}`).forEach((node) => node.remove())
    })
  }

  clearHover() {
    window.clearTimeout(this.hoverTimer)
    this.pendingRow = null

    if (this.hoverRow) {
      this.setPageHint(this.hoverRow, null)
      this.hoverRow = null
    }

    this.clearHighlight("hover")
  }

  clearPin() {
    if (this.pinnedRow) {
      delete this.pinnedRow.dataset.invoiceProvenancePinned
      this.pinnedRow = null
    }

    this.clearHighlight("pinned")
  }

  // A zoom change throws the page elements away, so re-draw whatever was pinned against
  // the new overlays. The match itself survives because it is stored in scale-1 units.
  restoreHighlights() {
    const row = this.pinnedRow
    this.clearHover()
    if (!row || !row.isConnected) {
      this.clearPin()
      return
    }

    this.clearHighlight("pinned")
    const match = this.matchesByRow.get(row)
    if (match?.state === "located") this.drawHighlight("pinned", match.token)
  }

  // Only meaningful on a located row: tells the user the match exists but is off-screen.
  setPageHint(row, pageNumber) {
    if (row.dataset.invoiceProvenanceState !== "located") return

    const badge = this.ensureBadge(row)
    if (!badge) return

    if (pageNumber === null) {
      badge.className = "invoice-provenance-badge invoice-is-hidden"
      badge.textContent = ""
      badge.removeAttribute("title")
      return
    }

    badge.className = "invoice-provenance-badge invoice-provenance-badge--page"
    badge.textContent = `on page ${pageNumber}`
    badge.title = "Click this row to scroll the PDF to it."
  }

  // The PDF pane scrolls internally, but it is also taller than the window, so the window
  // is usually the effective scroller. A highlight is only really on screen if it is
  // inside both boxes - testing the pane alone reports off-screen matches as visible.
  isHighlightVisible(highlight) {
    if (!this.hasPdfPaneTarget) return true

    const pane = this.pdfPaneTarget.getBoundingClientRect()
    const box = highlight.getBoundingClientRect()
    const top = Math.max(pane.top, 0)
    const bottom = Math.min(pane.bottom, window.innerHeight || pane.bottom)

    return box.bottom > top && box.top < bottom
  }

  // scrollIntoView walks every scrollable ancestor, so it handles the pane and the window
  // together. Doing the arithmetic by hand only moved whichever one we picked.
  scrollToHighlight(highlight) {
    highlight.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" })
  }

  rowFrom(node) {
    if (!(node instanceof Element) || !this.hasRowsTarget) return null

    const row = node.closest(".invoice-line-item:not(.invoice-line-item--new)")
    return row && this.rowsTarget.contains(row) ? row : null
  }
}
